import React from 'react';
import PageHero from '../components/ui/PageHero';
import { Shield, Database, Clock, Lock, Mail, UserCheck } from 'lucide-react';
import SEO from '../components/SEO';

const ConfidentialitePage = () => {
  return (
    <div>
      <SEO 
        title="Politique de Confidentialité"
        description="Politique de confidentialité de Mon Evénemenciel. Informations sur la collecte, l'utilisation et la protection de vos données personnelles (RGPD)."
        keywords="politique confidentialité, RGPD, protection données, Mon Evénemenciel"
        url="/confidentialite"
      />
      <PageHero
        title="Politique de Confidentialité"
        subtitle=""
        backgroundImage="/img/photo-evenement.webp"
      />

      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-gray max-w-none">
            
            {/* Introduction */}
            <div className="mb-10 bg-[#96452f]/5 rounded-xl p-6 border border-[#96452f]/20">
              <p className="text-brand-petrol/80">
                MonEvenemenciel accorde une grande importance à la protection de vos données personnelles et au respect de votre vie privée. Cette politique de confidentialité vous informe sur la manière dont nous collectons, utilisons et protégeons vos données personnelles, conformément au Règlement Général sur la Protection des Données (RGPD) et à la loi Informatique et Libertés.
              </p>
            </div>

            {/* Responsable du traitement */}
            <div className="mb-10">
              <h2 className="text-2xl font-display text-brand-petrol mb-4 flex items-center">
                <Shield className="w-6 h-6 text-[#96452f] mr-3" />
                Responsable du traitement
              </h2>
              <div className="bg-brand-ivory rounded-xl p-6">
                <p className="text-brand-petrol/70 mb-2">Le responsable du traitement des données est :</p>
                <ul className="text-brand-petrol/70 space-y-1 ml-4">
                  <li><strong>MonEvenemenciel SAS</strong></li>
                  <li>B36 Allée des Prêts Rouets, 69510 Messimy</li>
                  <li>Email : <a href="mailto:contact@monevenemenciel.eu" className="text-[#96452f] hover:underline">contact@monevenemenciel.eu</a></li>
                  <li>Téléphone : +33 6 12 93 84 63</li>
                </ul>
              </div>
            </div>

            {/* Données collectées */}
            <div className="mb-10">
              <h2 className="text-2xl font-display text-brand-petrol mb-4 flex items-center">
                <Database className="w-6 h-6 text-[#D08154] mr-3" />
                Données collectées
              </h2>
              <div className="bg-brand-ivory rounded-xl p-6">
                <p className="text-brand-petrol/70 mb-4">
                  Nous collectons les données personnelles que vous nous fournissez volontairement via notre formulaire de contact :
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h4 className="font-semibold text-brand-petrol mb-2">Données obligatoires *</h4>
                    <ul className="text-brand-petrol/70 text-sm space-y-1">
                      <li>• Nom et prénom</li>
                      <li>• Adresse email</li>
                      <li>• Sujet de votre demande</li>
                      <li>• Message</li>
                    </ul>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h4 className="font-semibold text-brand-petrol mb-2">Données facultatives</h4>
                    <ul className="text-brand-petrol/70 text-sm space-y-1">
                      <li>• Nom de l'entreprise</li>
                      <li>• Numéro de téléphone</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Finalités */}
            <div className="mb-10">
              <h2 className="text-2xl font-display text-brand-petrol mb-4 flex items-center">
                <UserCheck className="w-6 h-6 text-[#96452f] mr-3" />
                Finalités du traitement
              </h2>
              <div className="bg-brand-ivory rounded-xl p-6">
                <p className="text-brand-petrol/70 mb-4">
                  Vos données personnelles sont collectées et traitées uniquement pour les finalités suivantes :
                </p>
                <ul className="text-brand-petrol/70 space-y-2">
                  <li className="flex items-start">
                    <span className="text-[#96452f] mr-2">•</span>
                    Répondre à vos demandes de contact et d'information
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#96452f] mr-2">•</span>
                    Vous proposer nos services d'événementiel en entreprise
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#96452f] mr-2">•</span>
                    Établir des devis personnalisés
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#96452f] mr-2">•</span>
                    Assurer le suivi de notre relation commerciale
                  </li>
                </ul>
                <p className="text-brand-petrol/70 mt-4 text-sm italic">
                  Vos données ne sont jamais partagées avec des tiers à des fins commerciales.
                </p>
              </div>
            </div>

            {/* Durée de conservation */}
            <div className="mb-10">
              <h2 className="text-2xl font-display text-brand-petrol mb-4 flex items-center">
                <Clock className="w-6 h-6 text-[#D08154] mr-3" />
                Durée de conservation
              </h2>
              <div className="bg-brand-ivory rounded-xl p-6">
                <p className="text-brand-petrol/70">
                  Vos données personnelles sont conservées pendant une durée de <strong>3 ans</strong> à compter de votre dernier contact avec nous. Au-delà de cette période, vos données sont supprimées ou anonymisées.
                </p>
              </div>
            </div>

            {/* Sécurité */}
            <div className="mb-10">
              <h2 className="text-2xl font-display text-brand-petrol mb-4 flex items-center">
                <Lock className="w-6 h-6 text-[#96452f] mr-3" />
                Sécurité des données
              </h2>
              <div className="bg-brand-ivory rounded-xl p-6">
                <p className="text-brand-petrol/70 mb-4">
                  Nous mettons en œuvre toutes les mesures techniques et organisationnelles appropriées pour protéger vos données personnelles contre tout accès non autorisé, modification, divulgation ou destruction :
                </p>
                <ul className="text-brand-petrol/70 space-y-2">
                  <li className="flex items-start">
                    <span className="text-[#D08154] mr-2">✓</span>
                    Connexion sécurisée HTTPS
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#D08154] mr-2">✓</span>
                    Hébergement sécurisé chez un prestataire professionnel
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#D08154] mr-2">✓</span>
                    Accès restreint aux données personnelles
                  </li>
                </ul>
              </div>
            </div>

            {/* Vos droits */}
            <div className="mb-10">
              <h2 className="text-2xl font-display text-brand-petrol mb-4 flex items-center">
                <Mail className="w-6 h-6 text-[#D08154] mr-3" />
                Vos droits
              </h2>
              <div className="bg-brand-ivory rounded-xl p-6">
                <p className="text-brand-petrol/70 mb-4">
                  Conformément au RGPD, vous disposez des droits suivants concernant vos données personnelles :
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="bg-white rounded-lg p-3 border border-gray-200">
                    <strong className="text-brand-petrol">Droit d'accès</strong>
                    <p className="text-brand-petrol/70 text-sm">Obtenir une copie de vos données</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-gray-200">
                    <strong className="text-brand-petrol">Droit de rectification</strong>
                    <p className="text-brand-petrol/70 text-sm">Corriger vos données inexactes</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-gray-200">
                    <strong className="text-brand-petrol">Droit à l'effacement</strong>
                    <p className="text-brand-petrol/70 text-sm">Demander la suppression de vos données</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-gray-200">
                    <strong className="text-brand-petrol">Droit à la portabilité</strong>
                    <p className="text-brand-petrol/70 text-sm">Récupérer vos données dans un format lisible</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-gray-200">
                    <strong className="text-brand-petrol">Droit d'opposition</strong>
                    <p className="text-brand-petrol/70 text-sm">Vous opposer au traitement de vos données</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-gray-200">
                    <strong className="text-brand-petrol">Droit de limitation</strong>
                    <p className="text-brand-petrol/70 text-sm">Limiter le traitement de vos données</p>
                  </div>
                </div>
                <p className="text-brand-petrol/70 mt-4">
                  Pour exercer ces droits, contactez-nous à : <a href="mailto:contact@monevenemenciel.eu" className="text-[#96452f] hover:underline font-medium">contact@monevenemenciel.eu</a>
                </p>
              </div>
            </div>

            {/* Cookies */}
            <div className="mb-10">
              <h2 className="text-2xl font-display text-brand-petrol mb-4">
                🍪 Cookies
              </h2>
              <div className="bg-brand-ivory rounded-xl p-6">
                <p className="text-brand-petrol/70">
                  Notre site <strong>n'utilise pas de cookies de tracking ou de publicité</strong>. Nous n'utilisons que des cookies techniques strictement nécessaires au bon fonctionnement du site, qui ne nécessitent pas votre consentement préalable.
                </p>
              </div>
            </div>

            {/* Réclamation */}
            <div className="mb-10">
              <h2 className="text-2xl font-display text-brand-petrol mb-4">
                Réclamation
              </h2>
              <div className="bg-brand-ivory rounded-xl p-6">
                <p className="text-brand-petrol/70">
                  Si vous estimez que le traitement de vos données personnelles constitue une violation du RGPD, vous avez le droit d'introduire une réclamation auprès de la CNIL (Commission Nationale de l'Informatique et des Libertés) :
                </p>
                <p className="text-brand-petrol/70 mt-2">
                  <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-[#96452f] hover:underline">www.cnil.fr</a>
                </p>
              </div>
            </div>

            {/* Date de mise à jour */}
            <div className="text-center mt-12 pt-8 border-t border-gray-200">
              <p className="text-gray-500 text-sm">
                Dernière mise à jour : Janvier 2025
              </p>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default ConfidentialitePage;
